import type { KpiZone } from '@monitoring-panel/types';
import { ReportSection } from '@/components/report/ReportSection';
import { ProgressBar } from '@/components/ProgressBar';
import { KpiTile } from './KpiTile';
import { TableFrame, Th, Td, NumTd } from './TableFrame';
import { dayMonth, eur, formatMetric, num, pct } from './format';
import { zoneTagClass } from './labels';
import type { DeliveryReport, RecoKpi } from './types';

interface SummaryTabProps {
  report: DeliveryReport;
}

/** Pacing under this share of the expected delivery shows as an alert, as in the template. */
const PACING_ALERT_BELOW = 98;

const IMPRESSIONS_PER_CPM = 1000;

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function SummaryTab({ report }: SummaryTabProps) {
  return (
    <div className="space-y-4">
      <NavSection report={report} />
      <KpiZonesSection report={report} />
      <DeliverySection report={report} />
      <EstimateSection report={report} />
    </div>
  );
}

function NavSection({ report }: SummaryTabProps) {
  const { nav, plan, reco } = report;
  const hasNavLine = nav.lines.length > 0;
  const subtitle = hasNavLine
    ? `${nav.lines.join(', ')} · identificada por ${plan.nav.how}`
    : `Ninguna línea identificada · ${plan.nav.how}`;

  return (
    <ReportSection title="% en Navegación Activa" subtitle={subtitle}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          label={`% de ${reco.unit}`}
          value={pct(nav.units_pct)}
          tone={hasNavLine ? 'accent' : 'alert'}
        />
        <KpiTile label="% del gasto" value={pct(nav.cost_pct)} tone={hasNavLine ? 'default' : 'alert'} />
        <KpiTile
          label="% de las impresiones"
          value={pct(nav.imp_pct)}
          tone={hasNavLine ? 'default' : 'alert'}
        />
        <KpiTile
          label={`${capitalise(reco.unit)} en Nav. Activa`}
          value={num(nav.units)}
          hint={`coste ${eur(nav.cost, 0)}`}
          tone={hasNavLine ? 'default' : 'alert'}
        />
      </div>
    </ReportSection>
  );
}

function KpiZonesSection({ report }: SummaryTabProps) {
  const { reco, plan } = report;
  const bands = reco.bands;

  return (
    <ReportSection title="KPIs de la campaña vs objetivo" subtitle="Acumulado · mix de líneas activas · con la propuesta">
      {reco.kpis.length === 0 ? (
        <p className="text-adv-body-s text-adv-tertiary py-2">
          Ningún KPI con objetivo numérico: fíjalo en Advia OS o en el panel.
        </p>
      ) : (
        <TableFrame
          head={
            <>
              <Th>KPI</Th>
              <Th numeric>Objetivo</Th>
              <Th numeric>Banda ideal</Th>
              <Th numeric>Acumulado</Th>
              <Th numeric>Mix actual (activas)</Th>
              <Th numeric>Con la propuesta</Th>
            </>
          }
        >
          {reco.kpis.map((kpi) => (
            <KpiZoneRow key={kpi.id} kpi={kpi} report={report} />
          ))}
        </TableFrame>
      )}
      <p className="text-adv-body-s text-adv-tertiary mt-3">
        El conjunto de la campaña debe cumplir cada KPI con un margen ideal de{' '}
        <span className="adv-num">
          {num(bands.ideal_low, 1)}–{num(bands.ideal_high, 1)}×
        </span>{' '}
        el objetivo; más de <span className="adv-num">{num(bands.excess, 0)}×</span> es pagar
        calidad que nadie pidió. El conversion rate siempre se maximiza y, con los KPIs en banda, se
        minimiza el coste.
      </p>
      {plan.unmapped.length > 0 && (
        <div className="adv-alert mt-3 text-adv-body-s">
          KPI sin mapear en el plan de métricas:{' '}
          {plan.unmapped.map((item) => `${item.kpi} (${item.source})`).join(', ')}.
        </div>
      )}
    </ReportSection>
  );
}

function KpiZoneRow({ kpi, report }: { kpi: RecoKpi; report: DeliveryReport }) {
  const catalog = report.metrics_catalog;
  const band = kpi.zone?.band;
  const bandText = band
    ? `${formatMetric(catalog, kpi.id, band[0])} – ${formatMetric(catalog, kpi.id, band[1])}`
    : catalog[kpi.id]?.better === 'lower'
      ? '≤ objetivo'
      : 'maximizar';

  return (
    <tr>
      <Td className="font-bold">{kpi.label}</Td>
      <NumTd>{formatMetric(catalog, kpi.id, kpi.target)}</NumTd>
      <NumTd>{bandText}</NumTd>
      <ZoneCell value={formatMetric(catalog, kpi.id, kpi.value)} zone={kpi.zone} />
      <ZoneCell value={formatMetric(catalog, kpi.id, kpi.mix_now)} zone={kpi.mix_now_zone} />
      <ZoneCell value={formatMetric(catalog, kpi.id, kpi.proposed)} zone={kpi.proposed_zone} />
    </tr>
  );
}

function ZoneCell({ value, zone }: { value: string; zone: KpiZone | null }) {
  return (
    <NumTd>
      <span className="inline-flex items-center gap-2">
        {value}
        {zone && <span className={`adv-tag ${zoneTagClass(zone)} font-adv-sans`}>{zone.zone}</span>}
      </span>
    </NumTd>
  );
}

function DeliverySection({ report }: SummaryTabProps) {
  const { delivery, reco, exe } = report;
  const panel = delivery.panel;
  const unit = reco.unit;
  const completion =
    delivery.served !== null && delivery.contracted
      ? (delivery.served / delivery.contracted) * 100
      : null;
  const pacingTone = (value: number | null | undefined) =>
    value !== null && value !== undefined && value < PACING_ALERT_BELOW ? 'alert' : 'default';

  return (
    <ReportSection
      title="Entrega vs contratado"
      subtitle={`ingreso = ${unit} × ${eur(delivery.cpx)} (Advia OS)`}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="adv-card adv-card--kpi px-4 py-4 lg:col-span-1">
          <p className="adv-kpi__label">Servidos / contratados</p>
          <p className="mt-2 adv-num text-adv-body-l font-bold text-adv-primary">
            {num(delivery.served)} / {num(delivery.contracted)}
          </p>
          <ProgressBar value={completion ?? 0} showLabel={false} className="mt-3" />
          <p className="text-adv-body-s text-adv-tertiary mt-1">
            <span className="adv-num">{pct(completion)}</span> entregado
            {panel?.totalDays ? (
              <>
                {' · día '}
                <span className="adv-num">
                  {panel.daysPassed}/{panel.totalDays}
                </span>
              </>
            ) : null}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3 lg:col-span-2">
          <KpiTile
            label="Pacing acumulado"
            value={pct(panel?.accumulatedPacing)}
            tone={pacingTone(panel?.accumulatedPacing)}
          />
          <KpiTile label="Pacing ayer" value={pct(panel?.yesterdayPacing)} tone={pacingTone(panel?.yesterdayPacing)} />
          <KpiTile label="Run rate" value={pct(panel?.runRate)} tone={pacingTone(panel?.runRate)} />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
        <KpiTile
          label={`${capitalise(unit)}/día necesarios`}
          value={num(delivery.needed_per_day)}
          hint={`${num(delivery.remaining_days)} días restantes`}
          compact
        />
        <KpiTile
          label="Capacidad con topes actuales"
          value={delivery.capacity_per_day === null ? 'sin tope' : `${num(delivery.capacity_per_day)}/día`}
          hint={delivery.uncapped.length ? `sin tope: ${delivery.uncapped.join(', ')}` : undefined}
          compact
        />
        <KpiTile
          label={`Proyección a ${exe.end ? dayMonth(exe.end) : 'fin'}`}
          value={num(delivery.projected)}
          hint={gapText(delivery.gap, delivery.cpx, unit)}
          tone={delivery.gap !== null && delivery.gap > 0 ? 'alert' : 'default'}
          compact
        />
        <KpiTile
          label="Ingreso · coste · margen"
          value={`${eur(delivery.revenue, 0)} · ${eur(delivery.cost, 0)}`}
          hint={`margen ${eur(delivery.margin, 0)}${
            delivery.revenue ? ` (${pct(((delivery.margin ?? 0) / delivery.revenue) * 100)})` : ''
          }`}
          compact
        />
      </div>
    </ReportSection>
  );
}

/** Short of the contract is a lost revenue; over it, spare units. */
function gapText(gap: number | null, cpx: number | null, unit: string): string {
  if (gap === null) return 'sin proyección';
  if (gap > 0) return `faltan ${num(gap)} ${unit} · −${eur(gap * (cpx ?? 0), 0)}`;
  return `sobran ${num(-gap)} ${unit}`;
}

function EstimateSection({ report }: SummaryTabProps) {
  const { campaign, units_per_day: unitsPerDay, unit } = report.reco;
  const costLabel = unit === 'clicks' ? 'click' : 'unidad';
  // A cost per impression is a fraction of a cent: read per thousand, like a CPM.
  const perThousand = unit === 'impresiones';
  const scaleCost = (value: number | null) =>
    value === null ? null : perThousand ? value * IMPRESSIONS_PER_CPM : value;

  return (
    <ReportSection title="Estimación de la campaña" subtitle="ahora → con la propuesta">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          label="Gasto / día"
          value={`${eur(campaign.now.budget, 0)} → ${eur(campaign.after.budget, 0)}`}
          compact
        />
        <KpiTile
          label={`${capitalise(unit)} / día`}
          value={`${num(campaign.now.units)} → ${num(campaign.after.units)}`}
          hint={`necesarios ${num(unitsPerDay.needed)}`}
          compact
        />
        <KpiTile
          label={perThousand ? 'Coste por mil impresiones' : `Coste por ${costLabel}`}
          value={`${eur(scaleCost(campaign.cost_per_unit_now))} → ${eur(scaleCost(campaign.cost_per_unit_after))}`}
          compact
        />
        <KpiTile
          label="Ahorro estimado"
          value={`${eur(campaign.saving_day, 2)}/día`}
          hint={
            campaign.saving_flight
              ? `${eur(campaign.saving_flight, 0)} hasta fin (${num(campaign.remaining_days)} d)`
              : undefined
          }
          compact
        />
      </div>
    </ReportSection>
  );
}
